import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { isAdminOrStaff } from '@/utils/roleCheck';
import { Button } from '@/components/ui/button';

interface AudioDebugBadgeProps {
  moduleId: string;
  courseId: string;
  audioUrl: string | null;
  isLoading: boolean;
  error: string | null;
  loadTime?: number;
  cacheHit?: boolean;
}

/**
 * Audio Debug Badge - Admin/Staff only
 * Shows real-time audio status for debugging
 * Only visible to users with admin or staff role
 */
export const AudioDebugBadge: React.FC<AudioDebugBadgeProps> = ({
  moduleId,
  courseId,
  audioUrl,
  isLoading,
  error,
  loadTime,
  cacheHit
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Only show to admin/staff users (server-side role check)
  useEffect(() => {
    const checkVisibility = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsVisible(false);
        return;
      }
      
      // Check if user has admin or staff role (server-side validation)
      const hasAdminAccess = await isAdminOrStaff(user.id);
      setIsVisible(hasAdminAccess);
    };

    checkVisibility();
  }, []);

  if (!isVisible) return null;

  const getStatus = () => {
    if (error) return { label: 'Error', variant: 'destructive' as const };
    if (isLoading) return { label: 'Loading...', variant: 'secondary' as const };
    if (!audioUrl) return { label: 'No Audio', variant: 'outline' as const };
    return { label: 'Ready', variant: 'default' as const };
  };

  const status = getStatus();

  return (
    <div className="fixed top-20 right-4 z-40 opacity-60 hover:opacity-100 transition-opacity">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center gap-1 text-xs h-7 px-2 bg-background/90 backdrop-blur-sm"
      >
        <Activity className="h-3 w-3" />
        <Badge variant={status.variant} className="text-xs px-1 py-0">
          {status.label}
        </Badge>
        {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
      </Button>
      
      {!isCollapsed && (
        <div className="mt-1 space-y-1 bg-background/95 backdrop-blur-sm p-2 rounded border shadow-lg text-xs">
          {loadTime !== undefined && (
            <div className="text-muted-foreground">
              {loadTime}ms {cacheHit ? '(cached)' : '(fresh)'}
            </div>
          )}
          
          {error && (
            <Badge variant="destructive" className="block text-xs">
              {error}
            </Badge>
          )}
          
          <div className="text-muted-foreground">
            <div>Module: {moduleId.substring(0, 8)}...</div>
            <div>Course: {courseId.substring(0, 8)}...</div>
          </div>
        </div>
      )}
    </div>
  );
};
